/**
 * How every screen reads what grading used: time, tokens and the subscription's own figures.
 *
 * 🔴 USAGE, NOT A PRICE (owner, 2026-10-03: "we are using subscriptions so we should keep track
 * on usage not cost! and the amounts of tokens and time the grading took!"). Grading runs on a
 * Claude subscription, so a dollar figure for a run is a price nobody pays. What it spends is
 * tokens, minutes and the subscription's 5-hour and weekly allowance.
 *
 * 🔴 ONE SET OF FORMATTERS, SO A RUN READS THE SAME ON EVERY SCREEN. Runs, Grades, the report,
 * the Overview and Health all print a run's minutes and tokens, and the weekly movement; a run
 * that read "10.2 min" on one page and "612s" on another would be two claims about one run.
 *
 * ⚠️ THE MOVEMENT IS AN UPPER BOUND. The subscription is shared — every cohort grades against
 * it, and so does the owner's own Claude use in the same minutes — so how far a run moved the
 * weekly figure is at most what that run used. Every place that shows one says so (`UPPER`).
 *
 * ⚠️ NOT READ IS NEVER 0%. A grade from before the grader read the subscription, a gateway run,
 * and a reading whose window has reset since all have no figure, and each formatter here
 * answers `null` for them rather than a zero a reader would take as "nothing used".
 */
import type { Tokens, UsageReading, UsageWindows } from "./api";
import { DASH } from "./ui";

const NBSP = " ";

/** What one run's movement is, wherever it is shown. */
export const UPPER = "At most what this run used: the subscription is shared, and your own "
  + "Claude use in the same minutes counts too.";

/** Said where a run's weekly figure would be, for a run that read none. */
export const NOT_READ = "weekly not read";
export const NOT_READ_WHY = "Graded before the grader read the subscription’s usage";

/** 🔴 A FACT, NOT A SETTING (owner, 2026-10-03: variant B). The jail stops a run at
 *  `AGENT_MAX_TURNS` (60) and its `timeout 2700` (45 min) in `grade_submission.sh`; the console
 *  no longer sends `--max-budget-usd`, so there is no per-run dollar ceiling left to set. */
export const RUN_BOUND = `60${NBSP}turns${NBSP}· 45${NBSP}min`;

// ── time ──────────────────────────────────────────────────────────────────────

/** One run's wall clock: `10.2 min`. ⚠️ A run of two seconds is not `0.0 min`, which reads as
 *  nothing; it is `<0.1 min`. */
export function runMinutes(ms?: number | null): string {
  if (ms == null) return DASH;
  const m = ms / 60000;
  if (m > 0 && m < 0.05) return `<0.1${NBSP}min`;
  return `${m.toFixed(1)}${NBSP}min`;
}

/** A day's or a cohort's total, in whole minutes: `61 min`. */
export function totalMinutes(min?: number | null): string {
  if (min == null) return DASH;
  if (min > 0 && min < 0.5) return `<1${NBSP}min`;
  return `${Math.round(min)}${NBSP}min`;
}

// ── tokens ────────────────────────────────────────────────────────────────────

/** A count of one kind of token: `4.2k`, `38.1k`, `1.72M`. */
export function tokenCount(n?: number | null): string {
  if (n == null) return DASH;
  if (n >= 999_500) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1e3).toFixed(1)}k`;
  return String(n);
}

/** A run's tokens, in the one unit every list reads a run in: `1.86M`, `0.42M`. ⚠️ Below ten
 *  thousand it falls back to `tokenCount`, or a short run would read `0.00M`, which says none. */
export function runTokens(n?: number | null): string {
  if (n == null) return DASH;
  if (n >= 10_000) return `${(n / 1e6).toFixed(2)}M`;
  return tokenCount(n);
}

/** A day's total: `9.4M`. */
export function dayTokens(n?: number | null): string {
  if (n == null) return DASH;
  if (n >= 100_000) return `${(n / 1e6).toFixed(1)}M`;
  return tokenCount(n);
}

/** The four kinds a run spends, summed: the run's total. Null when nothing was recorded. */
export function tokenTotal(t?: Tokens | null): number | null {
  return t ? t.input + t.output + t.cache_read + t.cache_write : null;
}

// ── the subscription's figures ────────────────────────────────────────────────

/** A window's figure: `46%`.
 *  🔴 ROUNDED DOWN, NEVER UP. At 79.6% against a pause at 80% a rounded figure reads `80%`
 *  beside a card that is not paused; the figure shown is never past where the subscription
 *  stands. */
export function pct(n?: number | null): string {
  if (n == null) return DASH;
  return `${Math.floor(n + 1e-9)}%`;
}

const whole = (n: number) => Math.floor(n + 1e-9);

/** One window over one run: where it stood at the first reading and at the last, and how many
 *  readings there were. Runs carry this shape; the report's raw readings are turned into it. */
export interface Move { first: number | null; last: number | null; events: number }

/** A window's movement from a run's raw `{first, last, events}` readings, or null. */
export function moveOf(sub: UsageWindows | null | undefined,
                       key: "seven_day" | "five_hour"): Move | null {
  const at = (r?: UsageReading) => {
    const w = r?.[key];
    return typeof w?.pct === "number" ? w.pct : null;
  };
  const last = at(sub?.last);
  if (last == null) return null;
  return { first: at(sub?.first) ?? last, last, events: sub?.events ?? 1 };
}

/** Said for a window that reset while the run went, on Runs and in the report alike. */
export const RESET = "reset while it ran";

/** Whether the window reset while the run went: its last figure is below its first. A run
 *  cannot give allowance back, so a fall is a reset and never a movement. */
export function resetDuring(w?: Move | null): boolean {
  return !!w && w.last != null && w.first != null && w.events >= 2
    && whole(w.last) < whole(w.first);
}

/** `44% → 46%`, or `at 99%` when the run read the window once — where it stood, not how far
 *  it moved. Null when the run read nothing.
 *  🔴 A WINDOW THAT RESET WHILE THE RUN WENT IS `RESET`, AS `moveDelta` SAYS. `98% → 3%` beside
 *  "so this is at most what the run used" presented a fall as an upper bound on usage, and the
 *  report and Runs disagreed about one run. */
export function moveRange(w?: Move | null): string | null {
  if (!w || w.last == null) return null;
  if (w.first == null || w.events < 2) return `at ${pct(w.last)}`;
  if (resetDuring(w)) return RESET;
  return `${pct(w.first)} → ${pct(w.last)}`;
}

/** `+2%`, or `at 99%` for a single reading. ⚠️ THE DIFFERENCE OF THE TWO FIGURES SHOWN, so the
 *  delta on Runs and the range on the report can never disagree about one run. A window that
 *  reset while the run went has no delta, and says so rather than printing a fall. */
export function moveDelta(w?: Move | null): string | null {
  if (!w || w.last == null) return null;
  if (w.first == null || w.events < 2) return `at ${pct(w.last)}`;
  if (resetDuring(w)) return RESET;
  return `+${whole(w.last) - whole(w.first)}%`;
}

// ── when ──────────────────────────────────────────────────────────────────────

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

/** The viewer's own zone, short: `WEST`, `UTC`, `GMT+5:30`. Built as the deadline pill builds
 *  it (`Apply.tsx`'s `closesLabel`), in the viewer's own locale. */
export function zone(d: Date): string {
  return new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(d)
    .find((x) => x.type === "timeZoneName")?.value ?? "";
}

/** `09:00 WEST`, the reader's own clock. ⚠️ 24-HOUR, against the locale, as the deadline pill
 *  is: `12:59 AM` is the time of day people read backwards.
 *  🔴 THE ZONE IS NAMED, BECAUSE THE SERVER'S SENTENCES BESIDE IT SAY UTC. The pause sentence on
 *  the same card reads "— Sun 04 Oct 19:00 UTC" and this read "resets Sun 4 Oct, 20:00": one
 *  moment as two clock times, and only one of them said which zone it was in. */
export function clock(iso?: string | null): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (isNaN(+d)) return DASH;
  const t = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  const z = zone(d);
  return z ? `${t}${NBSP}${z}` : t;
}

/** `Tue 7 Oct, 09:00 WEST` — when a window resets. ⚠️ A FIXED SHAPE (`en-GB`) for the day and
 *  the clock, so it reads the same for every teacher; the zone is the viewer's own. */
export function dayAndTime(iso?: string | null): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (isNaN(+d)) return DASH;
  const day = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return `${day}, ${clock(iso)}`;
}

/** The time alone when it is today, `Tue 7 Oct, 09:00` when it is not. For the 5-hour window,
 *  which almost always resets later the same day. */
export function clockOrDay(iso?: string | null, now: number = Date.now()): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (isNaN(+d)) return DASH;
  return sameDay(d, new Date(now)) ? clock(iso) : dayAndTime(iso);
}

/** `12 minutes ago`, or `12 min ago` where the line is a figure rather than a sentence. */
export function since(iso?: string | null, now: number = Date.now(), short = false): string {
  if (!iso) return DASH;
  const t = +new Date(iso);
  if (isNaN(t)) return DASH;
  const s = Math.max(0, (now - t) / 1000);
  const say = (n: number, long: string, brief: string) =>
    short ? `${n}${NBSP}${brief} ago` : `${n} ${long}${n === 1 ? "" : "s"} ago`;
  const m = Math.round(s / 60);
  if (m < 1) return "under a minute ago";
  if (m < 90) return say(m, "minute", "min");
  const h = Math.round(s / 3600);
  if (h < 36) return say(h, "hour", "h");
  return say(Math.round(s / 86400), "day", "d");
}

// ── the one dollar figure a usage screen keeps ────────────────────────────────

/** Social activity's estimate: `$0.02`. It is paid per call, so it stays in dollars — and a
 *  fraction of a cent is `<$0.01`, never `$0.00`, which would read as free. */
export function paidDollars(n?: number | null): string {
  if (n == null) return DASH;
  if (n > 0 && n < 0.005) return "<$0.01";
  return `$${n.toFixed(2)}`;
}

// ── the gauge ─────────────────────────────────────────────────────────────────

/**
 * The weekly figure against the line where unattended grading pauses.
 *
 * ⚠️ THE TICK IS OUTSIDE THE TRACK, because `.meter` clips its fill to round its ends. A figure
 * that was not read draws an empty track with the line still on it: where the pause would fall
 * is true whether or not anybody has read the figure.
 * 🔴 0 MEANS STOP, and the label says so beside the line rather than leaving a tick at the far
 * left to be read as "never pauses".
 */
export function Gauge({ pct: at, pause, over }: {
  pct: number | null; pause: number; over: boolean;
}) {
  const fill = at == null ? 0 : Math.max(0, Math.min(100, at));
  const line = Math.max(0, Math.min(100, pause));
  const lbl = line > 86 ? "use-tick-lbl end" : line < 14 ? "use-tick-lbl start" : "use-tick-lbl";
  const said = at == null
    ? `Weekly usage not read; unattended grading pauses at ${pct(pause)}`
    : `${pct(at)} of the weekly allowance; unattended grading pauses at ${pct(pause)}`;
  return (
    <div className="use-gauge" role="img" aria-label={said} title={said}>
      <div className="meter">
        {at != null && <i className={over ? "use-fill over" : "use-fill"}
                          style={{ width: `${fill}%` }} />}
      </div>
      <span className="use-tick" style={{ left: `calc(${line}% - 1px)` }} />
      <span className={lbl} style={{ left: `${line}%` }}>
        pauses at {pct(pause)}{pause <= 0 && " — which means stop"}</span>
    </div>
  );
}

/**
 * What one run used, as a table cell: time first, then tokens, then — on Runs — how far the
 * weekly figure moved while it ran.
 *
 * ⚠️ A RUN WITH NOTHING MEASURED IS A DASH, not `— · — tok`: a run still going, or one refused
 * before it started, has no envelope yet.
 */
export function UsageCell({ ms, tokens, weekly, gateway, showWeekly }: {
  ms: number | null | undefined; tokens: number | null | undefined;
  weekly?: Move | null;
  /** 🔴 THE SERVER'S OWN BOOLEAN (`usage.gateway`), never a route compared with a word here:
   *  no stored route is spelled `gateway`, and that comparison matched no run there has been. */
  gateway?: boolean; showWeekly: boolean;
}) {
  if (ms == null && tokens == null && !weekly) return <span className="muted">{DASH}</span>;
  const move = moveDelta(weekly);
  return (
    <>
      <span className="num">{runMinutes(ms)}</span>
      <div className="small muted">
        {runTokens(tokens)}{NBSP}tok
        {showWeekly && <>{" · "}
          {move != null
            ? <>weekly <b title={resetDuring(weekly) ? undefined : UPPER}>{move}</b></>
            : gateway
              ? <span title="A gateway model is paid per token and does not draw on the
subscription.">via the gateway</span>
              : <span title={NOT_READ_WHY}>{NOT_READ}</span>}
        </>}
      </div>
    </>
  );
}
